/**
 * Analytics Service — Cross-project aggregate metrics.
 *
 * Fetches all jobs/assignments/batches for an org and computes
 * per-project and org-wide analytics.
 */

import { supabase } from '../lib/supabase';
import { safeQuery } from '../utils/safeSupabase';
import { CLIENT_VISIBLE_BATCH_STATUSES, computeAverageReviewQuality, computeBatchRollup, isActiveProjectStatus } from '../utils/projectRollups';
import { resolveAssignmentRate } from '../utils/projectMetrics';

/**
 * Fetch all data needed for cross-project analytics.
 * Single set of queries — no per-job iteration.
 *
 * @param {string} orgId
 * @returns {Object} { jobs, assignments, batches, qualityLogs }
 */
export async function fetchAnalyticsData(orgId) {
  if (!orgId) return { jobs: [], assignments: [], batches: [], qualityLogs: [], errors: {}, partialFailure: false };

  const [jobsRes, assignRes, batchRes, qualityRes] = await Promise.all([
    safeQuery(supabase.from('job').select('id, title, domain, status, client_display_name, team_size, budget, start_date, end_date, sla_delivery_date, created_at').eq('org_id', orgId).order('created_at', { ascending: false }), 'analytics-jobs'),
    safeQuery(supabase.from('assignment').select('id, job_id, individual_id, status, hours_logged, rate, quality_score, assigned_at, completed_at').eq('org_id', orgId), 'analytics-assignments'),
    safeQuery(supabase.from('annotation_batch').select('id, job_id, status, total_items, items_completed, items_accepted, items_rejected, qa_score, is_overlap, started_at, submitted_at, assigned_at, qa_completed_at, created_at').eq('org_id', orgId).eq('is_overlap', false), 'analytics-batches'),
    safeQuery(supabase.from('quality_log').select('id, job_id, individual_id, accuracy_percent, created_at').eq('org_id', orgId).not('accuracy_percent', 'is', null), 'analytics-qualityLogs'),
  ]);

  const errors = {
    jobs: jobsRes.error?.message || null,
    assignments: assignRes.error?.message || null,
    batches: batchRes.error?.message || null,
    qualityLogs: qualityRes.error?.message || null,
  };

  return {
    jobs: jobsRes.data || [],
    assignments: assignRes.data || [],
    batches: batchRes.data || [],
    qualityLogs: qualityRes.data || [],
    errors,
    partialFailure: Object.values(errors).some(Boolean),
  };
}

/**
 * Compute per-project metrics from raw data.
 *
 * @param {Object} data - from fetchAnalyticsData
 * @returns {Array} [{ jobId, title, domain, client, status, teamSize, hours, cost, budget, budgetUtil, avgQuality, batchCount, batchesCompleted, totalItems, completedItems, completionPercent, costPerItem, scheduleStatus }]
 */
export function computeProjectMetrics(data) {
  const { jobs, assignments, batches, qualityLogs } = data;

  return jobs.map(job => {
    const jobAssigns = assignments.filter(a => a.job_id === job.id);
    const jobBatches = batches.filter(b => b.job_id === job.id);
    const jobQuality = qualityLogs.filter(q => q.job_id === job.id);

    const activeAssigns = jobAssigns.filter(a => a.status === 'Active' || a.status === 'Onboarding');
    const uniqueAssigneeCount = new Set(jobAssigns.map(a => a.individual_id).filter(Boolean)).size;
    const hours = jobAssigns.reduce((s, a) => s + (a.hours_logged || 0), 0);
    // R9-closure audit fix: unified via resolveAssignmentRate so
    // AnalyticsDashboard totalCost agrees with internal dashboards.
    const cost = jobAssigns.reduce((s, a) => s + ((a.hours_logged || 0) * resolveAssignmentRate(a)), 0);
    const budget = job.budget ? parseFloat(job.budget) : null;
    const budgetUtil = budget && cost > 0 ? (cost / budget) * 100 : null;

    const batchRollup = computeBatchRollup(jobBatches, { visibleStatuses: CLIENT_VISIBLE_BATCH_STATUSES });
    const avgQuality = computeAverageReviewQuality({
      qualityLogs: jobQuality,
      batchQaScores: batchRollup.batchQaScores,
    });

    const totalItems = batchRollup.totalItems;
    const completedItems = batchRollup.completedItems;
    const completionPercent = batchRollup.completionPercent;
    const batchesCompleted = batchRollup.visibleBatches;
    const costPerItem = completedItems > 0 ? cost / completedItems : null;

    // Schedule status — deadline fallback: end_date || sla_delivery_date
    const deadline = job.end_date || job.sla_delivery_date;
    let scheduleStatus = null;
    if (deadline && totalItems > 0 && completedItems < totalItems) {
      const startedBatches = jobBatches.filter(b => b.started_at);
      if (startedBatches.length > 0) {
        const earliest = Math.min(...startedBatches.map(b => new Date(b.started_at).getTime()));
        const daysElapsed = (Date.now() - earliest) / 86400000;
        if (daysElapsed > 0.5) {
          const velocity = completedItems / daysElapsed;
          const remaining = totalItems - completedItems;
          const projectedDays = velocity > 0 ? remaining / velocity : Infinity;
          const daysToDeadline = (new Date(deadline).getTime() - Date.now()) / 86400000;
          scheduleStatus = projectedDays <= daysToDeadline ? 'on_track' : 'behind';
        }
      }
    } else if (job.status === 'Completed') {
      scheduleStatus = 'completed';
    }

    return {
      jobId: job.id,
      title: job.title,
      domain: job.domain,
      client: job.client_display_name,
      status: job.status,
      startDate: job.start_date,
      endDate: job.end_date || job.sla_delivery_date,
      teamSize: uniqueAssigneeCount,
      activeTeamSize: activeAssigns.length,
      hours: Math.round(hours * 10) / 10,
      cost: Math.round(cost * 100) / 100,
      budget,
      budgetUtil: budgetUtil != null ? Math.round(budgetUtil) : null,
      avgQuality: avgQuality != null ? Math.round(avgQuality * 10) / 10 : null,
      qualityObservationCount: jobQuality.length > 0
        ? jobQuality.length
        : batchRollup.batchQaScores.length,
      batchCount: batchRollup.batchCount,
      batchesCompleted,
      totalItems,
      completedItems,
      completionPercent,
      costPerItem: costPerItem != null ? Math.round(costPerItem * 1000) / 1000 : null,
      scheduleStatus,
    };
  });
}

/**
 * Compute org-wide aggregate totals.
 */
export function computeOrgTotals(projectMetrics) {
  const active = projectMetrics.filter(p => isActiveProjectStatus(p.status));
  const weightedQuality = projectMetrics.reduce((acc, project) => {
    if (project.avgQuality == null || !project.qualityObservationCount) return acc;
    acc.total += project.avgQuality * project.qualityObservationCount;
    acc.count += project.qualityObservationCount;
    return acc;
  }, { total: 0, count: 0 });

  return {
    totalProjects: projectMetrics.length,
    activeProjects: active.length,
    totalHours: Math.round(projectMetrics.reduce((s, p) => s + p.hours, 0) * 10) / 10,
    totalCost: Math.round(projectMetrics.reduce((s, p) => s + p.cost, 0) * 100) / 100,
    totalBudget: projectMetrics.reduce((s, p) => s + (p.budget || 0), 0),
    totalItems: projectMetrics.reduce((s, p) => s + p.totalItems, 0),
    completedItems: projectMetrics.reduce((s, p) => s + p.completedItems, 0),
    avgQuality: weightedQuality.count > 0
      ? Math.round((weightedQuality.total / weightedQuality.count) * 10) / 10
      : null,
    qualityObservationCount: weightedQuality.count,
    onTrack: active.filter(p => p.scheduleStatus === 'on_track').length,
    behind: active.filter(p => p.scheduleStatus === 'behind').length,
  };
}

/**
 * Compute weekly cost trend from assignments, bucketed per project for stacked/tooltip view.
 *
 * @param {Array} assignments - assignment rows (with job + individual)
 * @param {Object} options
 * @param {Set<string>} [options.jobIds] - scope to these jobs
 * @param {Object} [options.dateRange] - { start: Date, end: Date }; when omitted, shows last 8 weeks
 * @returns {Array<{ date, label, value, breakdown: [{ jobId, jobTitle, value, color }] }>}
 */
export function computeWeeklyCostTrend(assignments, options = {}) {
  const { jobIds = null, dateRange = null, jobTitleMap = {}, individualId = null } = options;
  let scopedAssignments = jobIds
    ? (assignments || []).filter(a => jobIds.has(a.job_id))
    : (assignments || []);
  // Optional per-annotator scope — used on the annotator dashboard to render
  // "Weekly Earnings Trend" (same function, filtered to their rows).
  if (individualId) scopedAssignments = scopedAssignments.filter(a => a.individual_id === individualId);
  const now = Date.now();

  // Determine window
  const rangeEnd = dateRange?.end ? new Date(dateRange.end).getTime() : now;
  const rangeStart = dateRange?.start
    ? new Date(dateRange.start).getTime()
    : rangeEnd - 8 * 7 * 86400000; // default 8 weeks back

  // Align to week boundaries (Sunday starts)
  const alignToSunday = (t) => {
    const d = new Date(t);
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() - d.getDay());
    return d.getTime();
  };

  const firstWeek = alignToSunday(rangeStart);
  const lastWeek = alignToSunday(rangeEnd);
  const weekMs = 7 * 86400000;
  const bucketCount = Math.max(1, Math.min(52, Math.round((lastWeek - firstWeek) / weekMs) + 1));

  const weeks = [];
  for (let i = 0; i < bucketCount; i++) {
    const weekStartTs = firstWeek + i * weekMs;
    const weekEndTs = weekStartTs + weekMs;
    const weekStart = new Date(weekStartTs);
    const label = weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const isoDate = weekStart.toISOString().slice(0, 10);

    const byJob = {};
    for (const a of scopedAssignments) {
      if (!a.assigned_at || !a.hours_logged) continue;
      // R9-closure audit fix: resolve rate via canonical resolver
      // (rate=null falls through to confirmed_rate). Previous `!a.rate`
      // guard dropped rate-null assignments entirely — skipping real
      // cost that confirmed_rate would have captured.
      const rate = resolveAssignmentRate(a);
      if (rate === 0) continue;
      const assignStart = new Date(a.assigned_at).getTime();
      const assignEnd = a.completed_at ? new Date(a.completed_at).getTime() : now;
      const totalDays = Math.max(1, (assignEnd - assignStart) / 86400000);
      const dailyCost = (a.hours_logged * rate) / totalDays;

      const overlapStart = Math.max(weekStartTs, assignStart);
      const overlapEnd = Math.min(weekEndTs, assignEnd);
      const overlapDays = Math.max(0, (overlapEnd - overlapStart) / 86400000);
      if (overlapDays <= 0) continue;

      const contribution = dailyCost * overlapDays;
      const jobId = a.job_id;
      if (!byJob[jobId]) byJob[jobId] = { jobId, jobTitle: jobTitleMap[jobId] || a.job?.title || jobId?.slice(0, 8) || '—', value: 0 };
      byJob[jobId].value += contribution;
    }

    const breakdown = Object.values(byJob).map(b => ({ ...b, value: Math.round(b.value) })).filter(b => b.value > 0);
    const value = breakdown.reduce((s, b) => s + b.value, 0);
    weeks.push({ date: isoDate, label, value, breakdown });
  }
  return weeks;
}

/**
 * Compute weekly annotations (items completed) trend, bucketed per project.
 *
 * Uses approved time_entry items_completed where available; falls back to
 * annotation_batch submitted_at + items_completed distribution.
 *
 * @param {Array} batches - annotation_batch rows
 * @param {Object} options
 * @param {Set<string>} [options.jobIds]
 * @param {Object} [options.dateRange]
 * @returns {Array<{ date, label, value, breakdown }>}
 */
export function computeWeeklyAnnotationsTrend(batches, options = {}) {
  const { jobIds = null, dateRange = null, jobTitleMap = {}, individualId = null } = options;
  let scopedBatches = jobIds
    ? (batches || []).filter(b => jobIds.has(b.job_id))
    : (batches || []);
  // Optional per-annotator scope for the annotator dashboard.
  if (individualId) scopedBatches = scopedBatches.filter(b => b.individual_id === individualId || b.assigned_to === individualId);
  const now = Date.now();

  const rangeEnd = dateRange?.end ? new Date(dateRange.end).getTime() : now;
  const rangeStart = dateRange?.start
    ? new Date(dateRange.start).getTime()
    : rangeEnd - 8 * 7 * 86400000;

  const alignToSunday = (t) => {
    const d = new Date(t);
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() - d.getDay());
    return d.getTime();
  };
  const firstWeek = alignToSunday(rangeStart);
  const lastWeek = alignToSunday(rangeEnd);
  const weekMs = 7 * 86400000;
  const bucketCount = Math.max(1, Math.min(52, Math.round((lastWeek - firstWeek) / weekMs) + 1));

  const weeks = [];
  for (let i = 0; i < bucketCount; i++) {
    const weekStartTs = firstWeek + i * weekMs;
    const weekEndTs = weekStartTs + weekMs;
    const weekStart = new Date(weekStartTs);
    const label = weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const isoDate = weekStart.toISOString().slice(0, 10);

    const byJob = {};
    for (const b of scopedBatches) {
      if (b.is_overlap) continue;
      if (!b.items_completed) continue;
      // Distribute items across the batch's active window. Prefer
      // started_at, but fall back to assigned_at → created_at when it's
      // not tracked. Many batches (ingest/seed paths, and any batch that
      // never had an explicit "start" event) carry assigned_at +
      // items_completed but NOT started_at — the old `if (!b.started_at)`
      // guard dropped every such batch, leaving the Weekly Annotations
      // Completed chart empty despite real completed items (dogfood F4).
      const startSource = b.started_at || b.assigned_at || b.created_at;
      if (!startSource) continue;
      const bStart = new Date(startSource).getTime();
      const bEnd = b.submitted_at ? new Date(b.submitted_at).getTime() : (b.qa_completed_at ? new Date(b.qa_completed_at).getTime() : now);
      const totalDays = Math.max(1, (bEnd - bStart) / 86400000);
      const dailyItems = (b.items_completed || 0) / totalDays;

      const overlapStart = Math.max(weekStartTs, bStart);
      const overlapEnd = Math.min(weekEndTs, bEnd);
      const overlapDays = Math.max(0, (overlapEnd - overlapStart) / 86400000);
      if (overlapDays <= 0) continue;

      const contribution = dailyItems * overlapDays;
      const jobId = b.job_id;
      if (!byJob[jobId]) byJob[jobId] = { jobId, jobTitle: jobTitleMap[jobId] || jobId?.slice(0, 8) || '—', value: 0 };
      byJob[jobId].value += contribution;
    }

    const breakdown = Object.values(byJob).map(b => ({ ...b, value: Math.round(b.value) })).filter(b => b.value > 0);
    const value = breakdown.reduce((s, b) => s + b.value, 0);
    weeks.push({ date: isoDate, label, value, breakdown });
  }
  return weeks;
}
